import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useGetUIBlocksQuery, UIBlock } from '../../api/uiBlocksApi';

const PickerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
  padding: 20px;
`;

const PickerModal = styled.div`
  background: white;
  border-radius: 12px;
  width: 90vw;
  max-width: 800px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const PickerHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e1e5e9;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PickerTitle = styled.h3`
  margin: 0;
  color: #1f2937;
  font-size: 18px;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const PickerContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  border-bottom: 1px solid #e1e5e9;
`;

const CategoryTab = styled.button<{ active: boolean }>`
  padding: 8px 16px;
  border: none;
  background: none;
  border-bottom: 2px solid ${props => props.active ? '#3b82f6' : 'transparent'};
  color: ${props => props.active ? '#3b82f6' : '#6b7280'};
  font-weight: ${props => props.active ? 600 : 400};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #3b82f6;
  }
`;

const BlocksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
`;

const BlockCard = styled.div`
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #3b82f6;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  }
`;

const BlockPreview = styled.div`
  height: 120px;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 14px;
`;

const BlockInfo = styled.div`
  padding: 12px;
`;

const BlockName = styled.h4`
  margin: 0 0 4px 0;
  color: #1f2937;
  font-size: 14px;
  font-weight: 600;
`;

const BlockCategory = styled.span`
  background: #f3f4f6;
  color: #6b7280;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  text-transform: capitalize;
`;

const EmptyState = styled.div`
  text-align: center;
  color: #6b7280;
  padding: 40px 20px;
`;

interface BlockPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectBlock: (block: UIBlock) => void;
  allowedCategories?: string[];
}

const categories = [
  { id: 'all', name: 'All Blocks' },
  { id: 'hero', name: 'Hero' },
  { id: 'features', name: 'Features' },
  { id: 'pricing', name: 'Pricing' },
  { id: 'testimonials', name: 'Testimonials' },
  { id: 'contact', name: 'Contact' },
  { id: 'footer', name: 'Footer' },
  { id: 'navigation', name: 'Navigation' },
];

export const BlockPicker: React.FC<BlockPickerProps> = ({
  isOpen,
  onClose,
  onSelectBlock,
  allowedCategories,
}) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { data: blocks = [], isLoading, error } = useGetUIBlocksQuery({});

  const filteredBlocks = blocks.filter(block => {
    // Filter out container blocks (they shouldn't be nested inside other containers)
    if (block.isContainer) return false;
    
    // Filter by allowed categories if specified
    if (allowedCategories && allowedCategories.length > 0) {
      return allowedCategories.includes(block.category || '');
    }
    
    // Filter by selected category
    if (selectedCategory === 'all') return true;
    return block.category === selectedCategory;
  });

  const handleBlockSelect = (block: UIBlock) => {
    onSelectBlock(block);
    onClose();
  };

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <PickerOverlay onClick={handleBackdropClick}>
      <PickerModal onClick={(e) => e.stopPropagation()}>
        <PickerHeader>
          <PickerTitle>Choose a Block</PickerTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </PickerHeader>

        <PickerContent>
          <CategoryTabs>
            {categories.map(category => (
              <CategoryTab
                key={category.id}
                active={selectedCategory === category.id}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.name}
              </CategoryTab>
            ))}
          </CategoryTabs>

          {isLoading ? (
            <EmptyState>Loading blocks...</EmptyState>
          ) : error ? (
            <EmptyState>Error loading blocks</EmptyState>
          ) : filteredBlocks.length === 0 ? (
            <EmptyState>No blocks found in this category</EmptyState>
          ) : (
            <BlocksGrid>
              {filteredBlocks.map(block => (
                <BlockCard
                  key={block.id}
                  onClick={() => handleBlockSelect(block)}
                >
                  <BlockPreview>
                    {block.name}
                  </BlockPreview>
                  <BlockInfo>
                    <BlockName>{block.name}</BlockName>
                    <BlockCategory>{block.category}</BlockCategory>
                  </BlockInfo>
                </BlockCard>
              ))}
            </BlocksGrid>
          )}
        </PickerContent>
      </PickerModal>
    </PickerOverlay>
  );
};